/**
 * Inter-Entity Relationship Builder - Builds relationships between different entity types
 *
 * Constructs relationship chains between entities in multi-entity queries,
 * handling both direct and indirect (multi-hop) relationship paths.
 *
 * @module queryBuilderCore/relationships/interEntityBuilder
 */

import { buildNodeFromBlock } from '@/sections/insights/queryBuilder/legacy/builders/nodeBuilder';
import {
  EntityBlock,
  LogicalOperator,
  Relationship,
} from '@/sections/insights/queryBuilder/legacy/types/types';

import { buildFallbackRelationship } from './fallbackRelationships';
import { relationshipPaths } from './relationshipPaths';

/**
 * Builds inter-entity relationships for multi-entity queries
 *
 * Iterates through entity blocks and creates appropriate relationship structures
 * based on defined relationship paths between entity types.
 *
 * @param rootNode - Root node to attach relationships to
 * @param validBlocks - All valid entity blocks in the query
 * @param interGroupOperators - Logical operators between entity blocks
 * @param assetFilterOptions - Available asset filter options
 * @param vulnFilterOptions - Available vulnerability filter options
 * @param technologyFilterOptions - Available technology filter options
 * @param seedFilterOptions - Available seed filter options
 * @param integrationValues - List of integration source values
 * @param queryContentTab - Current query content tab context
 */
export function buildInterEntityRelationships(
  rootNode: { relationships: Relationship[] },
  validBlocks: EntityBlock[],
  interGroupOperators: LogicalOperator[],
  assetFilterOptions?: Record<string, unknown>,
  vulnFilterOptions?: Record<string, unknown>,
  technologyFilterOptions?: Record<string, unknown>,
  seedFilterOptions?: Record<string, unknown>,
  integrationValues?: string[],
  queryContentTab?: string
): void {
  for (let i = 1; i < validBlocks.length; i++) {
    const nextBlock = validBlocks[i];
    if (!nextBlock || !nextBlock.entityType) {
      continue;
    }

    const nextNode = buildNodeFromBlock(
      nextBlock,
      assetFilterOptions,
      vulnFilterOptions,
      technologyFilterOptions,
      seedFilterOptions,
      integrationValues,
      queryContentTab
    );
    if (!nextNode) {
      continue;
    }

    const operatorIndex = i - 1;
    const isOrRelationship = interGroupOperators[operatorIndex] === 'OR';

    const currentEntityType = validBlocks[i - 1].entityType;
    const nextEntityType = nextBlock.entityType;

    const pathKey = `${currentEntityType}->${nextEntityType}`;
    const relationshipConfig = relationshipPaths[pathKey];

    if (relationshipConfig && relationshipConfig.length > 0) {
      const pathConfig = relationshipConfig[0].path;

      if (pathConfig.length === 1) {
        const rel = pathConfig[0];

        if (rel.direction === 'forward') {
          const relationship: Relationship = {
            label: rel.label,
            target: {
              labels: nextNode.labels,
              filters: nextNode.filters,
              relationships: nextNode.relationships,
            },
            optional: isOrRelationship,
            select_one: true,
          };
          if (rel.length !== undefined) {
            relationship.length = rel.length;
          }

          rootNode.relationships.push(relationship);
        } else {
          const relationship: Relationship = {
            label: rel.label,
            source: {
              labels: nextNode.labels,
              filters: nextNode.filters,
              relationships: nextNode.relationships,
            },
            optional: isOrRelationship,
            select_one: true,
          };
          if (rel.length !== undefined) {
            relationship.length = rel.length;
          }

          rootNode.relationships.push(relationship);
        }
      } else {
        let currentTarget = {
          labels: nextNode.labels,
          filters: nextNode.filters,
          relationships: nextNode.relationships,
        };

        for (let step = pathConfig.length - 1; step >= 0; step--) {
          const rel = pathConfig[step];

          if (step === 0) {
            if (rel.direction === 'forward') {
              rootNode.relationships.push({
                label: rel.label,
                target: currentTarget,
                optional: isOrRelationship,
                select_one: true,
              });
            } else {
              rootNode.relationships.push({
                label: rel.label,
                source: currentTarget,
                optional: isOrRelationship,
                select_one: true,
              });
            }
          } else {
            const intermediateNode = {
              labels: [rel.intermediate || 'Attribute'],
              filters: [],
              relationships:
                rel.direction === 'forward'
                  ? [
                      {
                        label: rel.label,
                        target: currentTarget,
                        select_one: true,
                      },
                    ]
                  : [
                      {
                        label: rel.label,
                        source: currentTarget,
                        select_one: true,
                      },
                    ],
            };

            currentTarget = intermediateNode;
          }
        }
      }
    } else {
      const fallbackRelationship = buildFallbackRelationship(
        currentEntityType,
        nextEntityType,
        nextNode,
        isOrRelationship
      );

      if (fallbackRelationship) {
        rootNode.relationships.push(fallbackRelationship);
      }
    }
  }
}
